import { formatMoney, getAccountMoney } from '../../../shared/money/exact-money.js';
import { isPendingVerificationAccount } from './account-platform-state.js';

function humanizeFieldName(name) {
  const raw = String(name || '').trim();
  if (!raw) {
    return '-';
  }

  return raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
}

function normalizeFieldValue(value) {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function buildProfileName(profile) {
  const firstName = pickFirst(profile?.firstName, profile?.first_name);
  const lastName = pickFirst(profile?.lastName, profile?.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return fullName || pickFirst(profile?.email, profile?.uid, profile?.id) || '-';
}

function extractAccountFinance(account) {
  return (
    account?.sync ||
    account?.tpAccountSync ||
    account?.tpAccountSyncDTO ||
    account?.tp_account_sync ||
    account?.tp_account_sync_dto ||
    null
  );
}

function extractVisibleAccountFields(account) {
  if (!account || typeof account !== 'object') {
    return [];
  }

  const hiddenFields = new Set([
    'uid',
    'capabilities',
    'terminallogin',
    'profile',
    'tradingserverid',
    'trading_server_id',
    'assigned',
    'balance',
    'type',
    'currency',
    'sync',
    'tpaccountsync',
    'tpaccountsyncdto',
    'tp_account_sync',
    'tp_account_sync_dto',
  ]);

  return Object.entries(account).filter(([key]) => !hiddenFields.has(String(key || '').toLowerCase()));
}

function buildProfileSummary(account) {
  const profile = account?.profile && typeof account.profile === 'object' ? account.profile : null;
  const profileUid = String(pickFirst(profile?.uid, profile?.id) || '').trim();
  const profilePath = profileUid ? `/profiles/${encodeURIComponent(profileUid)}` : '';
  const displayName = profile ? buildProfileName(profile) : '-';

  const fields = [
    ['name', 'Name', displayName],
    ['email', 'Email', profile?.email ?? null],
    ['phone', 'Phone', profile?.phone ?? null],
    ['country', 'Country', profile?.country ?? null],
    ['type', 'Type', profile?.type ?? null],
    ['created', 'Created', profile?.created ?? null],
  ].map(([key, label, value]) => ({
    key,
    label,
    value: normalizeFieldValue(value),
    linkTo: key === 'name' ? profilePath : '',
    isJson: typeof value === 'object' && value !== null,
  }));

  return {
    displayName,
    status: normalizeFieldValue(profile?.status),
    path: profilePath,
    fields,
  };
}

function buildFinanceFields(account, locale) {
  const source = extractAccountFinance(account) || {};

  return [
    ['balance', formatMoney(getAccountMoney(account, 'balance'), locale, { fallback: '-', trimTrailingZeros: true })],
    ['freeMargin', formatMoney(getAccountMoney(account, 'freeMargin'), locale, { fallback: '-', trimTrailingZeros: true })],
    ['usedMargin', formatMoney(getAccountMoney(account, 'usedMargin'), locale, { fallback: '-', trimTrailingZeros: true })],
    ['equity', formatMoney(getAccountMoney(account, 'equity'), locale, { fallback: '-', trimTrailingZeros: true })],
    ['leverage', source.leverage ?? null],
    ['lastSuccessfulSyncAt', source.lastSuccessfulSyncAt ?? source.last_successful_sync_at ?? null, 'Last sync'],
  ];
}

export function buildAccountDetailsModel(account, locale) {
  const source = account ? { ...account, login: account.terminalLogin ?? account.login, accountNumber: account.accountNumber ?? account.login } : {};
  const isPendingVerification = isPendingVerificationAccount(source);
  const basicFields = extractVisibleAccountFields(source).map(([key, value]) => ({
    key,
    label: humanizeFieldName(key),
    value: isPendingVerification && String(key || '').toLowerCase() === 'login'
      ? ''
      : normalizeFieldValue(value),
    badge: isPendingVerification && String(key || '').toLowerCase() === 'login'
      ? 'pendingVerification'
      : '',
    isJson: typeof value === 'object' && value !== null,
  }));

  const financeFields = buildFinanceFields(source, locale).map(([key, value, label]) => ({
    key,
    label: label || humanizeFieldName(key),
    value: normalizeFieldValue(value),
    isJson: typeof value === 'object' && value !== null,
  }));

  return {
    type: String(source?.type || '-').toUpperCase(),
    currency: String(source?.currency || '-'),
    isPendingVerification,
    basicFields,
    financeFields,
    profile: buildProfileSummary(source),
  };
}
